export class Cache {
    constructor(options) {
        this.cache = new Map();
        this.options = options || { sweepInterval: 100 };
        if (this.options.idleDurationMs != undefined && this.options.idleDurationMs <= 0) {
            throw new Error('Idle duration must be positive');
        }
        this.interval = this.options.sweepInterval || 100;
        if (this.interval <= 0) {
            throw new Error('Sweep interval must be a positive number');
        }
    }
    // can't use Log.error directly in sweep-cache as it'd be a circular dependency, so leaving a way to set a logging callback from outside (environment-cloud)
    static setLogIntervalError(loggerCallback) {
        Cache.logIntervalError = loggerCallback;
    }
    put(key, value, time, expireCallback, idleDurationMs, expiringTimeBuffer, expiringCallback) {
        if (time === undefined || time === null || time <= 0) {
            throw new Error('Cache timeout must be a positive number');
        }
        if (idleDurationMs === undefined || idleDurationMs === null || idleDurationMs <= 0) {
            idleDurationMs = this.options.idleDurationMs;
        }
        const record = {
            value: value,
            lastUsed: idleDurationMs ? Date.now() : undefined,
            expire: time + Date.now(),
            idleDurationMs: idleDurationMs,
            expireCallback: expireCallback,
            expiringTriggerTime: expiringTimeBuffer + Date.now(),
            expiringCallback: expiringCallback
        };
        this.cache.set(key, record);
        if (!this.timeout) {
            this.timeout = setInterval(this.onInterval.bind(this), this.interval);
            // In node.js, this timer should be .unref'd.
            // This function doesn't exist in the client
            if (this.timeout.unref) {
                this.timeout.unref();
            }
        }
        return value;
    }
    del(key) {
        if (this.options.delCallback) {
            const record = this.cache.get(key);
            if (record) {
                this.options.delCallback(key, record.value);
            }
        }
        const retVal = this.cache.delete(key);
        // Turn off the timer
        if (this.size() === 0) {
            this.clear();
        }
        return retVal;
    }
    clear() {
        if (this.timeout) {
            clearInterval(this.timeout);
            this.timeout = undefined;
        }
        this.cache.clear();
    }
    get(key) {
        let record = this.cache.get(key);
        if (record) {
            if (this.options.idleDurationMs) {
                record.lastUsed = Date.now();
            }
            // there's a chance of having a race condition when the record has already expired but the interval hasn't
            // had a chance to run yet to delete the record. So, checking here if it's still up-to-date
            if (record.expire < Date.now()) {
                this.del(key);
                if (record.expireCallback) {
                    record.expireCallback(key, record.value);
                }
                // checking it one more time as we have the cases when `expireCallback` automatically refreshes the record,
                // so we need to never have it expired (see secretStore.ts for example)
                record = this.cache.get(key);
                if (!record) {
                    return undefined;
                }
            }
            return record.value;
        }
        return undefined;
    }
    /**
     * Does not work for IE. You will need a polyfill for Map if you want to use this on IE.
     * Use forEach instead.
     */
    keys() {
        return this.cache.keys();
    }
    forEach(cb) {
        this.cache.forEach((record, key) => {
            cb(record.value, key);
        });
    }
    size() {
        return this.cache.size;
    }
    updateExpireTime(key, timeToLive) {
        if (this.cache.has(key) && timeToLive >= 0) {
            this.cache.get(key).expire = timeToLive + Date.now();
            return true;
        }
        return false;
    }
    onInterval() {
        const now = Date.now();
        this.cache.forEach((record, key) => {
            try {
                if (record.idleDurationMs) {
                    if (record.lastUsed < now - record.idleDurationMs) {
                        this.del(key);
                        if (this.options.idleCallback) {
                            this.options.idleCallback(key, record.value);
                        }
                        return; // Ensure we don't fire an expire callback
                    }
                }
                if (record.expire < now) {
                    this.del(key);
                    if (record.expireCallback) {
                        record.expireCallback(key, record.value);
                    }
                }
                // calls callback if record is expiring within buffer time
                if (record.expiringTriggerTime < now) {
                    if (record.expiringCallback) {
                        record.expiringCallback(key, record.value);
                        record.expiringCallback = undefined;
                    }
                }
            }
            catch (err) {
                if (Cache.logIntervalError) {
                    Cache.logIntervalError(err);
                }
            }
        });
    }
}
//# sourceMappingURL=cache.js.map